import * as React from 'react';
import { useHistory, RouteComponentProps } from 'react-router';
import master, { ShapeTypes } from '../Master';
import { Loading } from '../Loading';
import { ConfirmDialog, GraphQLErrorDialog } from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { SectionHeader } from '../components/SectionHeader';
import {
  useGetPlaceCodesLazyQuery,
  useDeletePlaceCodeMutation,
  useUpdatePlaceCodesDisplayOrderMutation,
  useGetPlaceCodeMarkerDependenciesLazyQuery,
  useGetPlaceSymbolSvgsLazyQuery,
} from '../generated/graphql';
import './PlaceCodesPage.css';
import { AuthButton } from '../components/Button';
import { ToastContext } from '../components/Toast';
import { getMarkerUsages } from '../utils/MarkerHelper';
import { TableScroll } from '../components/TableScroll';
import { orderByIds } from '../utils/reorder';
import {
  getReorderTableStorageKey,
  getTableColumnCount,
  ReorderHandleCell,
  ReorderHandleHeader,
  useListReorder,
} from '../components/ReorderMode';
import { ListSummary } from '../components/ListControls';

type IPlaceCodesPageProps = RouteComponentProps<{ placeId: string }>;

type Code = {
  id: number;
  name: string;
};

/**
 * 表の列数。0件の行を表の幅いっぱいに広げるために使う。
 * 並びを変えられる人には左端に取っ手の列が加わるので、0件の行の幅は getTableColumnCount で数える
 */
const COLUMN_COUNT = 5;

/** 補修コードの編集に必要な権限の段階。編集・削除のボタン（AuthButton）と同じ段階にする */
const EDIT_AUTHORITY_LEVEL = 2;

export const PlaceCodesPage: React.FC<IPlaceCodesPageProps> = function (props) {
  const placeId = parseInt(props.match.params.placeId);

  const toastContext = React.useContext(ToastContext);
  const history = useHistory();
  const [
    getPlaceCodes,
    { called, data, loading, error },
  ] = useGetPlaceCodesLazyQuery({
    variables: { placeId },
  });
  const [deleteTarget, setDeleteTarget] = React.useState<Code>();
  const [
    getMarkerDependencies,
    markerDepenenciesResult,
  ] = useGetPlaceCodeMarkerDependenciesLazyQuery();
  const [deletePlaceCode, deletePlaceCodeResult] = useDeletePlaceCodeMutation({
    onCompleted() {
      toastContext.showToast('削除しました', 'success');
      getPlaceCodes();
    },
    onError() {
      toastContext.showToast('削除できませんでした。時間をおいて再実行してください。', 'error');
    },
  });
  const [updateDisplayOrder] = useUpdatePlaceCodesDisplayOrderMutation();
  // データを読み込む前は空の並びを渡し、読み込んだ後はサーバーが返した並び順を渡す。
  // 取っ手を落とすとすぐに保存し、保存の通知と「元に戻す」はこのフックが出す
  const reorderMode = useListReorder((data?.getPlace.codes ?? []).map((code) => code.id), {
    authLv: EDIT_AUTHORITY_LEVEL,
    filtering: false,
    save: (ids) => updateDisplayOrder({ variables: { placeId, codeIds: ids } }),
  });
  const [getPlaceSymbolSvgs, getPlaceSymbolSvgsResult] = useGetPlaceSymbolSvgsLazyQuery();


  if (!called) {
    getPlaceCodes();
    getPlaceSymbolSvgs({ variables: { placeId } });
  }

  if (error) {
    return <GraphQLErrorDialog error={error} />;
  }

  if (markerDepenenciesResult.error) {
    return <GraphQLErrorDialog error={markerDepenenciesResult.error} />;
  }

  if (loading || !data) {
    return <Loading />;
  }

  const place = data.getPlace;
  const displayedCodes = orderByIds(place.codes, reorderMode.displayedIds);

  let deleteDialog;
  if (deleteTarget && !markerDepenenciesResult.loading && markerDepenenciesResult.data) {
    const markers = markerDepenenciesResult.data.getCodeMarkerDependencies;
    let usageWarning;
    if (markers.length > 0) {
      const usages = getMarkerUsages(markers);
      usageWarning = (
        <p className="PlaceCodesPage-delete_warning">
          この補修コードは図面上の図形で使用されています（使用中: {usages.used} 件、履歴: {usages.total} 件）。削除すると、これらの図形に影響します。
        </p>
      );
    }

    deleteDialog = (
      <ConfirmDialog
        open={true}
        danger={true}
        title="補修コードの削除"
        message={
          <div>
            <p>「{deleteTarget.name}」を削除しますか？</p>
            {usageWarning}
          </div>
        }
        yesLabel="削除"
        onYes={() => {
          deletePlaceCode({
            variables: { placeCodeId: deleteTarget.id },
          });
          setDeleteTarget(undefined);
        }}
        onNo={() => setDeleteTarget(undefined)}
      />
    );
  }

  let rows;
  if (displayedCodes.length === 0) {
    rows = <EmptyRow colSpan={getTableColumnCount(reorderMode, COLUMN_COUNT)} message="補修コードが登録されていません" />;
  } else {
    rows = displayedCodes.map((code, index) => {
      const shape = master.Shapes.find(
        (s) => s.id === code.shape_scheme_id
      );
      if (!shape) {
        throw new Error('unknown');
      }

      let shapeSubType;
      switch (shape.id) {
        case ShapeTypes.Symbol:
          const symbolSvg = getPlaceSymbolSvgsResult.data?.getPlaceSymbolSvgs.find(
            (s) => s.id === code.shape_symbol_scheme_id
          );
          shapeSubType = symbolSvg?.name;
          break;
      }

      // 並びを変えられる人には、行の左端に取っ手の列を置く。操作の列（編集・コピー・削除）はいつも出す
      let handleCell;
      if (reorderMode.handleColumn) {
        handleCell = <ReorderHandleCell {...reorderMode.getHandleCellProps(code.id, index)} />;
      }
      const editPath = `/places/${placeId}/codes/${code.id}/edit`;
      const copyPath = `/places/${placeId}/codes/new?copyFrom=${code.id}`;
      const actionCell = (
        <td className="App-table_button_cell">
          <div className="App-table_actions">
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color="white"
              onClick={() => history.push(editPath)}
            >
              編集
            </AuthButton>
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color="white"
              onClick={() => history.push(copyPath)}
            >
              コピー
            </AuthButton>
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color="red"
              onClick={() => {
                setDeleteTarget(code);
                getMarkerDependencies({
                  variables: { placeCodeId: code.id },
                });
              }}
            >
              削除
            </AuthButton>
          </div>
        </td>
      );

      return (
        <tr key={code.id} {...reorderMode.getRowProps(code.id)}>
          {handleCell}
          <td>{code.id}</td>
          <td>{code.name}</td>
          <td>
            {shape.name} {shapeSubType}
          </td>
          <td>{code.selected_work?.name ?? '施工管理なし'}</td>
          {actionCell}
        </tr>
      );
    });
  }

  // 見出しの右のボタン
  const headerActions = (
    <>
      <AuthButton
        authLv={EDIT_AUTHORITY_LEVEL}
        mini={true}
        onClick={() => history.push(`/places/${placeId}/codes/new`)}
      >
        新規作成
      </AuthButton>
    </>
  );

  // 並びを変えられる人には、取っ手の列を左端に置き、見出しは空にして幅を変えられないようにする
  let handleHeader;
  if (reorderMode.handleColumn) {
    handleHeader = <ReorderHandleHeader />;
  }
  const actionHeader = <th>操作</th>;

  const tableStorageKey = getReorderTableStorageKey(reorderMode, 'placeCodes');

  return (
    <div className="PlaceCodesPage-container">
      <Loading
        open={deletePlaceCodeResult.loading || markerDepenenciesResult.loading}
      />
      {deleteDialog}
      <SectionHeader title="補修コード" actions={headerActions} />
      <ListSummary reorder={reorderMode} shownCount={place.codes.length} />
      {/* 取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する */}
      <TableScroll key={tableStorageKey} storageKey={tableStorageKey}>
        <table className="App-table">
          <thead>
            <tr>
              {handleHeader}
              <th>ID</th>
              <th>補修コード</th>
              <th>図形の種類</th>
              <th>工種</th>
              {actionHeader}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
